import { useState, useRef, useEffect, useCallback, memo } from 'react';
import { FileUpload } from '../../api';
import { formatFileSize } from '../../utils/fileHash';
import { getFileType, FileIcon, highlightMatch } from '../../../../shared/src';
import { SelectionBar } from './SelectionBar';
import { createMultiDragGhost } from './createMultiDragGhost';

interface FileListProps {
  files: FileUpload[];
  selectedFileId?: string;
  onSelect: (file: FileUpload) => void;
  onRename?: (fileId: string, newName: string) => Promise<void>;
  onDelete?: (file: FileUpload) => void;
  onDragStart?: (e: React.DragEvent, file: FileUpload) => void;
  onDragEnd?: () => void;
  // Multi-select props
  multiSelectedIds: Set<string>;
  hasMultiSelection: boolean;
  onMultiSelectToggle: (id: string, event: React.MouseEvent) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onDeleteSelected: () => void;
  deletingSelected?: boolean;
  // Pagination
  totalFiles: number;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMoreError: string | null;
  onLoadMore: () => void;
  // Search: active term to highlight, and a handler to jump to a result's folder
  // (only set on project-wide search, where each row carries a folder_path).
  searchTerm?: string;
  onNavigateToFolder?: (folderId: string | null) => void;
}

const FolderPathIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
  </svg>
);

interface FileItemProps {
  file: FileUpload;
  isSelected: boolean;
  onSelect: (file: FileUpload) => void;
  onRename?: (fileId: string, newName: string) => Promise<void>;
  onDelete?: (file: FileUpload) => void;
  onDragStart?: (e: React.DragEvent, file: FileUpload) => void;
  onDragEnd?: () => void;
  // Multi-select props
  isMultiSelected: boolean;
  hasAnyMultiSelection: boolean;
  // Stable accessor for the current multi-selection (read only at drag time).
  // Passing this instead of the Set keeps FileItem's props stable so React.memo
  // can skip re-rendering rows unaffected by a selection change (P5).
  getSelectedIds: () => string[];
  onCheckboxChange: (id: string, event: React.MouseEvent) => void;
  // Search
  searchTerm?: string;
  onNavigateToFolder?: (folderId: string | null) => void;
}

const FileItem = memo(function FileItem({
  file,
  isSelected,
  onSelect,
  onRename,
  onDelete,
  onDragStart,
  onDragEnd,
  isMultiSelected,
  hasAnyMultiSelection,
  getSelectedIds,
  onCheckboxChange,
  searchTerm,
  onNavigateToFolder,
}: FileItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const fileType = getFileType(file.content_type, file.file_name);

  // Get file extension
  const getExtension = (name: string) => {
    const match = name.match(/\.[^.]+$/);
    return match ? match[0] : '';
  };

  // Get name without extension
  const getBaseName = (name: string) => {
    const ext = getExtension(name);
    return name.slice(0, name.length - ext.length);
  };

  const startRename = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onRename) return;
    setEditName(getBaseName(file.file_name));
    setIsEditing(true);
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleRename = async () => {
    const trimmedName = editName.trim();
    const extension = getExtension(file.file_name);
    const newFullName = trimmedName + extension;

    if (!trimmedName || newFullName === file.file_name) {
      setIsEditing(false);
      return;
    }

    setSaving(true);
    try {
      await onRename?.(file.id, newFullName);
      setIsEditing(false);
    } catch (err) {
      console.error('Rename failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRename();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.(file);
  };

  const handleDragStart = (e: React.DragEvent) => {
    if (onDragStart) {
      e.dataTransfer.effectAllowed = 'move';

      const selectedIds = isMultiSelected ? getSelectedIds() : [];
      const isMultiDrag = isMultiSelected && selectedIds.length > 1;

      // If this item is part of a multi-selection, drag all selected items
      if (isMultiDrag) {
        e.dataTransfer.setData('application/x-file-ids', selectedIds.join(','));
        e.dataTransfer.setData('application/x-file-id', file.id);
      } else {
        e.dataTransfer.setData('application/x-file-id', file.id);
      }

      const target = e.currentTarget as HTMLElement;
      const rect = target.getBoundingClientRect();
      const cursorOffset = { x: e.clientX - rect.left, y: e.clientY - rect.top };

      if (isMultiDrag) {
        // Finder-style stacked ghost with count badge
        const ghost = createMultiDragGhost(target, selectedIds.length, cursorOffset);
        e.dataTransfer.setDragImage(ghost.element, ghost.offsetX, ghost.offsetY);
        setTimeout(() => document.body.removeChild(ghost.element), 0);
      } else {
        // Single item: simple scaled clone
        const scale = 0.85;
        const clone = target.cloneNode(true) as HTMLElement;
        clone.style.cssText = `
          position: absolute;
          top: -9999px;
          left: -9999px;
          width: ${rect.width * scale}px;
          height: ${rect.height * scale}px;
          opacity: 0.7;
          pointer-events: none;
          background: var(--bg-secondary, #f9fafb);
          border-radius: 8px;
          overflow: hidden;
        `;
        document.body.appendChild(clone);
        clone.offsetHeight;

        const offsetX = cursorOffset.x * scale;
        const offsetY = cursorOffset.y * scale;
        e.dataTransfer.setDragImage(clone, offsetX, offsetY);
        setTimeout(() => document.body.removeChild(clone), 0);
      }

      onDragStart(e, file);
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if (isEditing) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey) {
      onCheckboxChange(file.id, e);
    } else if (hasAnyMultiSelection) {
      onCheckboxChange(file.id, e);
    } else {
      onSelect(file);
    }
  };

  return (
    <div
      className={`file-item ${isSelected ? 'file-item-selected' : ''} ${isMultiSelected ? 'file-item-multi-selected' : ''}`}
      onClick={handleCardClick}
      draggable={!!onDragStart && !isEditing}
      onDragStart={handleDragStart}
      onDragEnd={onDragEnd}
    >
      {/* Multi-select checkbox */}
      <div
        className={`item-checkbox ${hasAnyMultiSelection ? 'checkbox-visible' : 'checkbox-hover-only'} ${isMultiSelected ? 'checkbox-checked' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          onCheckboxChange(file.id, e);
        }}
      >
        {isMultiSelected && (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </div>

      <div className="file-icon">
        <FileIcon type={fileType} />
      </div>

      {isEditing ? (
        <div className="file-name-edit" onClick={(e) => e.stopPropagation()}>
          <input
            ref={inputRef}
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={handleRename}
            onKeyDown={handleKeyDown}
            disabled={saving}
          />
          <span className="file-extension">{getExtension(file.file_name)}</span>
        </div>
      ) : (
        <div className="file-name" title={file.file_name}>
          {highlightMatch(file.file_name, searchTerm)}
        </div>
      )}

      {/* Renamed file matched on its original name — show why it's a result. */}
      {searchTerm &&
        file.file_name.toLowerCase().indexOf(searchTerm.toLowerCase()) === -1 &&
        file.original_name.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1 && (
          <div className="file-original-name" title={`Originally: ${file.original_name}`}>
            originally {highlightMatch(file.original_name, searchTerm)}
          </div>
        )}

      {/* Project-wide search: show (and link to) where the match lives. */}
      {file.folder_path !== undefined && (
        <button
          type="button"
          className="file-folder-path"
          title={`Go to ${file.folder_path || 'project root'}`}
          onClick={(e) => {
            e.stopPropagation();
            onNavigateToFolder?.(file.folder_id);
          }}
        >
          <FolderPathIcon />
          <span className="file-folder-path-text">{file.folder_path || 'Project root'}</span>
        </button>
      )}

      <div className="file-meta">
        <span className="file-size">{formatFileSize(file.file_size)}</span>
      </div>

      {file.description && <div className="file-has-description" title="Has description" />}

      {(onRename || onDelete) && !isEditing && (
        <div className="file-actions">
          {onRename && (
            <button className="file-action-btn" onClick={startRename} title="Rename">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          )}
          {onDelete && (
            <button className="file-action-btn file-action-delete" onClick={handleDeleteClick} title="Delete">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
});

export function FileList({
  files,
  selectedFileId,
  onSelect,
  onRename,
  onDelete,
  onDragStart,
  onDragEnd,
  multiSelectedIds,
  hasMultiSelection,
  onMultiSelectToggle,
  onSelectAll,
  onClearSelection,
  onDeleteSelected,
  deletingSelected,
  totalFiles,
  hasMore,
  isLoadingMore,
  loadMoreError,
  onLoadMore,
  searchTerm,
  onNavigateToFolder,
}: FileListProps) {
  const showCaption = totalFiles > files.length;

  // Stable accessor for the current selection so FileItem (memoized) doesn't
  // re-render every row when the selection Set's identity changes (P5).
  const selectedIdsRef = useRef(multiSelectedIds);
  selectedIdsRef.current = multiSelectedIds;
  const getSelectedIds = useCallback(() => Array.from(selectedIdsRef.current), []);

  return (
    <div className="file-section">
      <h4 className="section-title">
        Files
        {showCaption && (
          <span className="file-section-count">
            {' '}
            (showing {files.length.toLocaleString()} of {totalFiles.toLocaleString()})
          </span>
        )}
      </h4>
      {hasMultiSelection && (
        <SelectionBar
          count={multiSelectedIds.size}
          totalCount={totalFiles}
          itemLabel="file"
          onSelectAll={onSelectAll}
          onClearSelection={onClearSelection}
          onDeleteSelected={onDeleteSelected}
          deleting={deletingSelected}
          selectAllLabel="Select all"
        />
      )}
      <div className="file-grid">
        {files.map((file) => (
          <FileItem
            key={file.id}
            file={file}
            isSelected={file.id === selectedFileId}
            isMultiSelected={multiSelectedIds.has(file.id)}
            hasAnyMultiSelection={hasMultiSelection}
            getSelectedIds={getSelectedIds}
            onCheckboxChange={onMultiSelectToggle}
            onSelect={onSelect}
            onRename={onRename}
            onDelete={onDelete}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            searchTerm={searchTerm}
            onNavigateToFolder={onNavigateToFolder}
          />
        ))}
      </div>
      {hasMore && (
        <div className="file-load-more">
          {loadMoreError && (
            <p className="file-load-more-error" role="alert">
              {loadMoreError}
            </p>
          )}
          <button
            className="btn-secondary file-load-more-btn"
            onClick={onLoadMore}
            disabled={isLoadingMore}
            aria-busy={isLoadingMore}
          >
            {isLoadingMore
              ? 'Loading…'
              : loadMoreError
                ? `Retry — load more (${files.length.toLocaleString()} of ${totalFiles.toLocaleString()})`
                : `Load more (${files.length.toLocaleString()} of ${totalFiles.toLocaleString()})`}
          </button>
        </div>
      )}
    </div>
  );
}
